import { ListSearchButton } from '@ui/ListSearchButton';
import { ElementListHeaderLayout } from './ElementListHeaderLayout';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ToggleIcon } from '@ui/ToggleIcon';
import { Icon } from '@ui/Icon';
import MultiLineTooltip from '@ui/MultiLineTooltip';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_LIST_GROUP_BY_CODE } from '@shared/constants';
import { useElementListGroupBy } from '@features/elements/helpers/element-grouping.helpers';

import {
  useGroupByCategory,
  setGroupByCategory,
} from '@features/products/group-by-category.store';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';
import { currencyIcons } from '@ui/Icon/Icon';
import {
  useAnyRowShowingColumns,
  setElementListColumnsHovered,
} from './element-list-column-hover.store';
import ElementListSearchField from './ElementListSearchField';
import { ElementListSortControl } from './ElementListSortControl';
import {
  ImportTabHeaderConversion,
  useImportUnusedColumnsToggleVisible,
  useShowUnusedColumns,
} from '@features/csv-import';
import { useProjectState } from '@client/store/project';
import { useElementListSearchField } from './use-element-list-search-field.hook';
import { useBuildHeaderStickyReplace } from './use-build-header-sticky-replace.hook';
import { PillTabs } from '@ui/PillTabs';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useSelectedElementId, useSelectedVersion } from '@client/store/ui';
import {
  getElementById,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { useGroupedElementCategories } from '@features/elements/hooks/use-grouped-element-categories.hook';
import { useAddElement } from '@client/store/project';
import { ElementCategoryMenu } from '@features/elements/components/ElementCategoryMenu/ElementCategoryMenu';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { getSbefCodeFromElementOrRecipe } from '@shared/helpers/sbef.helpers';
import { placementForNewElementSbefCode } from '@shared/helpers/element-property-values.helpers';
import { resolveVisibleSbefCodeForNewElement } from '@shared/helpers/sbef-results.helpers';
import { useSelectedSbefGroups } from '@features/sbef/sbef-groups.store';
import { useExpandedItems } from '@client/hooks/use-expand-elements.hooks';
import { requestScrollToElement } from './element-scroll-request.store';
import { ElementKind } from '@shared/models/project.model';
import { OneOfFactoryElements } from '@shared/models/factory-element.model';

const BASE_TABS = ['elements', 'products', 'imports'] as const;
const ALL_TABS = ['elements', 'products', 'actions', 'imports'] as const;
const SEARCH_TOOLTIP = navigator.userAgent.includes('Mac')
  ? 'Search (⌘F)'
  : 'Search (Ctrl+F)';

type ListTab = (typeof ALL_TABS)[number];

type ElementListHeaderProps = {
  selectedTab?: ListTab;
  onTabChange?: (tab: ListTab) => void;
  /** When true, renders the "Actions" tab (only while a proposal is active). */
  showActionsTab?: boolean;
  /** When false, hides the Imports tab (CSV drop still works on the build view). */
  showImportTab?: boolean;
  isSearchOpen?: boolean;
  openSearch?: () => void;
  fieldProps?: ReturnType<typeof useElementListSearchField>['fieldProps'];
  /** SBEF group row currently selected in the element list (drives new-element placement). */
  selectedGroupKey?: string | null;
};

const ElementListHeader: React.FC<ElementListHeaderProps> = ({
  selectedTab = 'elements',
  onTabChange,
  showActionsTab = false,
  showImportTab = true,
  isSearchOpen: isSearchOpenProp,
  openSearch: openSearchProp,
  fieldProps: fieldPropsProp,
  selectedGroupKey = null,
}) => {
  const tabs = (showActionsTab ? ALL_TABS : BASE_TABS).filter(
    (tab) => showImportTab || tab !== 'imports',
  );
  const { classes } = useStyles();
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const isStickyActive = useBuildHeaderStickyReplace(stickySentinelRef);
  const local = useElementListSearchField();
  const isSearchOpen = isSearchOpenProp ?? local.isSearchOpen;
  const openSearch = openSearchProp ?? local.openSearch;
  const fieldProps = fieldPropsProp ?? local.fieldProps;

  const readonly = useIsReadonly();
  const addElement = useAddElement();
  const selectedVersion = useSelectedVersion();
  const selectedElementId = useSelectedElementId();
  const [createElementAnchor, setCreateElementAnchor] =
    useState<HTMLElement | null>(null);
  const selectedElement = useMemo(() => {
    if (!selectedVersion || !selectedElementId) return undefined;
    const element = getElementById(selectedVersion, selectedElementId);
    return isElement(element) ? element : undefined;
  }, [selectedElementId, selectedVersion]);
  const selectedElementSbefCode = useMemo(
    () =>
      selectedElement
        ? getSbefCodeFromElementOrRecipe(selectedElement)
        : undefined,
    [selectedElement],
  );
  const categoryGroups = useGroupedElementCategories(selectedElement);
  const selectedSbefGroups = useSelectedSbefGroups();
  const { set: setGroupExpanded } = useExpandedItems({ defaultPrefix: 'sbef' });

  const handleOpenCreateElementMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setCreateElementAnchor(event.currentTarget);
    },
    [],
  );

  const handleCloseCreateElementMenu = useCallback(() => {
    setCreateElementAnchor(null);
  }, []);

  const handleAddElement = useCallback(
    async (categoryId: ElementCategoryID) => {
      if (!selectedVersion) return;

      const target = selectedElement ?? selectedVersion;
      const options = selectedElement
        ? { isSibling: true, placement: 'after' as const }
        : undefined;

      const defaults: OneOfFactoryElements = {
        kind: ElementKind.Element,
        category_id: categoryId,
      };

      // Keep the new element in a group the scope selector currently shows, otherwise it lands in
      // a hidden group and is invisible after creation (DEV-3507). Seeded on the factory so the
      // element is created with the code — a follow-up write would render it in "Other" first and
      // visibly jump once the code landed. Skip categories with no sbef_code row (DEV-3962).
      const newSbefCode = resolveVisibleSbefCodeForNewElement({
        selectedGroupKey,
        selectedElementSbefCode,
        selectedSbefGroups,
      });
      const { propertyValues, groupKey } = placementForNewElementSbefCode(
        categoryId,
        newSbefCode,
      );

      const { element } = await addElement(target, defaults, {
        ...options,
        ...(propertyValues ? { propertyValues } : {}),
      });

      if (!isElement(element)) return;

      // Expand the group the stored code lands in. No code folds into Site operations.
      setGroupExpanded(groupKey, true);
      requestScrollToElement(element.id);

      handleCloseCreateElementMenu();
    },
    [
      addElement,
      handleCloseCreateElementMenu,
      selectedElement,
      selectedElementSbefCode,
      selectedGroupKey,
      selectedSbefGroups,
      setGroupExpanded,
      selectedVersion,
    ],
  );

  return (
    <>
      <Box
        ref={stickySentinelRef}
        className={classes.stickySentinel}
        aria-hidden
      />
      <ElementListHeaderLayout
        className={isStickyActive ? classes.rootStuck : undefined}
        left={
          <>
            {isSearchOpen ? (
              <ElementListSearchField {...fieldProps} />
            ) : (
              /* Default state — icon + tabs */
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pl: '4px',
                  height: '100%',
                  width: '100%',
                }}
              >
                <ListSearchButton
                  onClick={openSearch}
                  tooltip={SEARCH_TOOLTIP}
                />

                <PillTabs
                  tabs={tabs}
                  value={selectedTab}
                  onChange={(tab) => onTabChange?.(tab)}
                />

                {selectedTab === 'elements' && !readonly && (
                  <Tooltip
                    title="New element"
                    placement="bottom"
                    disableInteractive
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={handleOpenCreateElementMenu}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ml: 'auto',
                        mr: '8px',
                        width: 20,
                        height: 20,
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        border: 'none',
                        borderRadius: '5px',
                        backgroundColor: 'transparent',
                        color: 'rgba(0,0,0,0.55)',
                        cursor: 'pointer',
                        p: 0,

                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.05)',
                          color: 'rgba(0,0,0,0.85)',
                        },
                      }}
                    >
                      <Icon category="mui" icon="Add" size={13} />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}

            <ElementCategoryMenu
              anchor={createElementAnchor}
              categoryGroups={categoryGroups}
              onSelect={handleAddElement}
              onClose={handleCloseCreateElementMenu}
            />
          </>
        }
      >
        {selectedTab === 'imports' ? (
          <ImportTabHeaderControls />
        ) : (
          <ElementListHeaderControls selectedTab={selectedTab} />
        )}
      </ElementListHeaderLayout>
    </>
  );
};

/**
 * Compact toggle used in the element-list header (e.g. "Group by code",
 * "Show unused columns"). 18px on/off icon plus a dimmed label, both
 * sharing a single click target so clicking either flips state.
 */
const HeaderToggle: React.FC<{
  checked: boolean;
  label: string;
  onToggle: () => void;
}> = ({ checked, label, onToggle }) => {
  const { classes } = useToggleStyles();
  return (
    <Box
      onClick={onToggle}
      data-toggle-control
      className={classes.toggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      <ToggleIcon checked={checked} />
      <Typography className={classes.label}>{label}</Typography>
    </Box>
  );
};

const ImportTabHeaderControls: React.FC = () => {
  const { project } = useProjectState('project');
  const showToggle = useImportUnusedColumnsToggleVisible(project?.id);
  const { showUnusedColumns, setShowUnusedColumns } = useShowUnusedColumns();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        width: '100%',
      }}
    >
      {showToggle && (
        <HeaderToggle
          checked={showUnusedColumns}
          label="Show unused columns"
          onToggle={() => setShowUnusedColumns(!showUnusedColumns)}
        />
      )}
      <ImportTabHeaderConversion />
    </Box>
  );
};

type ElementListHeaderControlsProps = {
  selectedTab: ListTab;
};

const ElementListHeaderControls: React.FC<ElementListHeaderControlsProps> = ({
  selectedTab,
}) => {
  const [groupBy, setGroupBy] = useElementListGroupBy();
  const groupByCategory = useGroupByCategory();
  const displayCurrency = useDisplayCurrency();
  const co2eVisible = useCo2eVisible();
  const anyRowActive = useAnyRowShowingColumns();

  const isProducts = selectedTab === 'products';
  const grouped = isProducts ? groupByCategory : groupBy === 'sbef';
  const handleToggleGroup = () => {
    const next = !grouped;
    if (isProducts) {
      setGroupByCategory(next);
      return;
    }
    amplitudeLog(AMP_ELEMENT_LIST_GROUP_BY_CODE, {
      groupBy: next ? 'sbef' : 'none',
    });
    setGroupBy(next ? 'sbef' : 'none');
  };

  return (
    <>
      <ElementListSortControl />
      <HeaderToggle
        checked={grouped}
        label={isProducts ? 'Group by category' : 'Group by code'}
        onToggle={handleToggleGroup}
      />

      {/* CO2 + Cost header cells — wrapper always catches hover, content toggles */}
      <Box
        onMouseEnter={() => setElementListColumnsHovered(true)}
        onMouseLeave={() => setElementListColumnsHovered(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          ml: 'auto',
        }}
      >
        {co2eVisible.isEnabled && (
          <Box
            sx={{
              width: 60,
              flexShrink: 0,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.4,
              visibility: anyRowActive ? 'visible' : 'hidden',
            }}
          >
            <MultiLineTooltip
              disableInteractive
              rows={['Carbon kgCO2e per m²']}
            >
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <Icon category="costEmission" icon="kgCO2e" size={18} />
              </Box>
            </MultiLineTooltip>
          </Box>
        )}

        {!displayCurrency.isDisabled && (
          <Box
            sx={{
              width: 60,
              flexShrink: 0,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.4,
              visibility: anyRowActive ? 'visible' : 'hidden',
            }}
          >
            <MultiLineTooltip
              disableInteractive
              rows={[`Expenses ${displayCurrency.current} per m²`]}
            >
              <Box
                sx={{
                  width: 'fit-content',
                  height: 'fit-content',
                  display: 'flex',
                }}
              >
                {currencyIcons[displayCurrency.current]}
              </Box>
            </MultiLineTooltip>
          </Box>
        )}
      </Box>
    </>
  );
};

export default ElementListHeader;

const useStyles = makeStyles()(({ palette }) => ({
  stickySentinel: {
    height: 0,
    width: '100%',
  },
  rootStuck: {
    borderBottom: `1px solid ${palette.neutral.lightest}`,
  },
}));

const useToggleStyles = makeStyles()(() => ({
  toggle: {
    cursor: 'pointer',
  },
  label: {
    fontSize: 9,
    fontWeight: 400,
    lineHeight: 1,
    color: 'rgba(0,0,0,0.6)',
    whiteSpace: 'nowrap',
  },
}));
